import { get, omit, set } from "lodash-es";
import pluralize from "pluralize";
import React, { FC, useState } from "react";
import { v4 as uuid } from "uuid";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Alert, Collapse, Grid, Paper, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";

import Header from "../../components/Header";
import Page from "../../components/Page";
import { QuestionMarkCircle } from "../../components/icons";
import { useSelector } from "../../store";
import { useGetClusterHealthQuery } from "../../store/pomeriumApi";
import ReRunHealthChecksButton from "../routes/components/ReRunHealthChecksButton";

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  return <IconButton {...omit(props, "expand")} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

interface Message {
  text: string;
  helpUrl: string;
}

interface Messages {
  success: Message[];
  error: Message[];
  warning: Message[];
}

interface Checks {
  messages: Messages;
}

const OKChecks: FC<Checks> = ({ messages }) => {
  const [expanded, setExpanded] = useState(false);
  if (!messages?.success?.length) {
    return <></>;
  }
  return (
    <Alert
      severity="success"
      sx={{
        "& .MuiAlert-message": { p: 0, flexGrow: 1 },
      }}
    >
      <Stack
        direction="row"
        onClick={() => setExpanded(!expanded)}
        sx={{ alignItems: "center" }}
      >
        <span style={{ flexGrow: 1 }}>
          Everything {messages?.error?.length ? "else " : " "}looks good:
        </span>
        <ExpandMore
          expand={expanded}
          aria-expanded={expanded}
          aria-label="show more"
        >
          <ExpandMoreIcon />
        </ExpandMore>
      </Stack>
      <Collapse in={expanded}>
        <ul>
          {messages?.success?.map((message) => {
            return <li key={uuid()}>{message.text}</li>;
          })}
        </ul>
      </Collapse>
    </Alert>
  );
};

const WarningChecks: FC<Checks> = ({ messages }) => {
  const [expanded, setExpanded] = useState(false);
  if (!messages?.warning?.length) {
    return <></>;
  }
  return (
    <Alert
      severity="warning"
      sx={{
        "& .MuiAlert-message": { p: 0, flexGrow: 1 },
      }}
    >
      <Stack
        direction="row"
        onClick={() => setExpanded(!expanded)}
        sx={{ alignItems: "center" }}
      >
        <span style={{ flexGrow: 1 }}>Warnings:</span>
        <ExpandMore
          expand={expanded}
          aria-expanded={expanded}
          aria-label="show more"
        >
          <ExpandMoreIcon />
        </ExpandMore>
      </Stack>
      <Collapse in={expanded}>
        <ul>
          {messages?.warning?.map((message) => {
            return <li key={uuid()}>{message.text}</li>;
          })}
        </ul>
      </Collapse>
    </Alert>
  );
};

const Status: FC = () => {
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const { data } = useGetClusterHealthQuery(
    {
      organizationId,
      clusterId,
    },
    { skip: !organizationId || !clusterId },
  );

  const initial: Record<string, Messages> = {
    $cluster: { success: [], error: [], warning: [] },
  };

  const healthChecks =
    data?.reduce((ob, current) => {
      const key: string =
        `['` +
        (current?.hostname || "$cluster") +
        `']['` +
        current.status +
        `']`;
      const arr: Message[] = get(ob, key, []) as Message[];
      arr.push({ text: current.description, helpUrl: current.helpUrl });
      set(ob, key, arr);
      return ob;
    }, initial) || initial;

  const { $cluster, ...replicas } = healthChecks;
  const numReplicas = Object.keys(replicas)?.length || 0;

  const actions = (
    <Stack direction="row" alignItems="center" spacing={2}>
      <ReRunHealthChecksButton />
    </Stack>
  );
  return (
    <Page title="Status">
      <Header title="Status" breadcrumbs={true} actions={actions} />
      <Stack spacing={1}>
        {$cluster.error.map((e) => {
          return (
            <Alert key={uuid()} severity="error">
              {e.text}
              {e.helpUrl && (
                <>
                  <sup id={uuid()}>
                    {" "}
                    <Button
                      color="primary"
                      sx={{
                        padding: 0,
                        fontWeight: "bold",
                        fontSize: `1rem`,
                        minWidth: 2,
                        fontFamily: "fontFamily",
                        "& svg": {
                          width: `16px`,
                          height: `16px`,
                        },
                      }}
                      target="_blank"
                      href={e.helpUrl}
                    >
                      <QuestionMarkCircle color="primary" />
                    </Button>
                  </sup>
                </>
              )}
            </Alert>
          );
        })}
        <WarningChecks messages={$cluster} />
        <OKChecks messages={$cluster} />
      </Stack>
      {!!numReplicas && (
        <>
          <Typography variant="h4" sx={{ my: 2 }}>
            {numReplicas} {pluralize("replica", numReplicas)} active
          </Typography>

          <Grid container direction="row" spacing={4}>
            {Object.keys(replicas).map((replica) => {
              const errors = replicas[replica]?.error || [];
              return (
                <Grid key={uuid()} size={6}>
                  <Paper>
                    <Typography
                      variant="h5"
                      sx={{ pt: 2, pb: 1, textAlign: "center" }}
                    >
                      {replica}
                    </Typography>
                    {errors.map((e) => {
                      return (
                        <Alert key={uuid()} severity="error">
                          {e.text}
                          {e.helpUrl && (
                            <>
                              <sup id={uuid()}>
                                {" "}
                                <Button
                                  color="primary"
                                  sx={{
                                    padding: 0,
                                    fontWeight: "bold",
                                    fontSize: `1rem`,
                                    minWidth: 2,
                                    fontFamily: "fontFamily",
                                    "& svg": {
                                      width: `16px`,
                                      height: `16px`,
                                    },
                                  }}
                                  target="_blank"
                                  href={e.helpUrl}
                                >
                                  <QuestionMarkCircle color="primary" />
                                </Button>
                              </sup>
                            </>
                          )}
                        </Alert>
                      );
                    })}
                    <WarningChecks messages={replicas[replica]} />
                    <OKChecks messages={replicas[replica]} />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </Page>
  );
};
export default Status;
